/**
 * MerchantListingsStatsBanner — top-of-page summary boxes for إدارة المعروضات.
 *
 * Pulls /api/admin/merchant-proxy/stats once on mount + every 30s.
 * Each box is a clickable shortcut that jumps to its tab.
 */
import { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Car, Phone, Building2, Store, CreditCard as PlateIcon, Layers, Loader2,
  AlertTriangle, RefreshCw,
} from 'lucide-react';
import { getAuthHeaders } from '../../../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
  cars:        { icon: Car,       ringFrom: 'from-orange-500/20',  ringTo: 'to-orange-500/0',  text: 'text-orange-300',  border: 'border-orange-500/30',  ar: 'السيارات',          en: 'Cars',           target: 'listings-cars' },
  car_plates:  { icon: PlateIcon, ringFrom: 'from-amber-500/20',   ringTo: 'to-amber-500/0',   text: 'text-amber-300',   border: 'border-amber-500/30',   ar: 'لوحات السيارات',    en: 'Car Plates',     target: 'listings-car-plates' },
  numbers:     { icon: Phone,     ringFrom: 'from-purple-500/20',  ringTo: 'to-purple-500/0',  text: 'text-purple-300',  border: 'border-purple-500/30',  ar: 'أرقام الموبايل والهواتف', en: 'Numbers',  target: 'listings-numbers' },
  real_estate: { icon: Building2, ringFrom: 'from-blue-500/20',    ringTo: 'to-blue-500/0',    text: 'text-blue-300',    border: 'border-blue-500/30',    ar: 'العقارات',          en: 'Real Estate',    target: 'listings-real-estate' },
  ecommerce:   { icon: Store,     ringFrom: 'from-emerald-500/20', ringTo: 'to-emerald-500/0', text: 'text-emerald-300', border: 'border-emerald-500/30', ar: 'المتاجر الإلكترونية', en: 'E-Commerce',  target: 'listings-ecommerce' },
};

const ORDER = ['cars', 'car_plates', 'numbers', 'real_estate', 'ecommerce'];

export default function MerchantListingsStatsBanner({ language = 'ar', onShortcut }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/merchant-proxy/stats`,
        { headers: getAuthHeaders() }
      );
      setStats(res.data);
    } catch (err) {
      const code = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
      setError({ code, detail });
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    fetchStats();
    const interval = setInterval(() => { if (alive) fetchStats(); }, 30000);
    return () => { alive = false; clearInterval(interval); };
  }, [fetchStats]);

  const total = stats?.grand_total ?? 0;
  const per = stats?.per_type || {};

  return (
    <div className="mb-6" data-testid="listings-stats-banner">
      {/* Error banner (shows when API call fails — replaces silent zeros) */}
      {error && !loading && (
        <div
          className="mb-3 p-3 rounded-xl bg-red-500/10 border border-red-500/30 flex items-start gap-3 flex-wrap"
          data-testid="listings-stats-error"
        >
          <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-red-300">
              {language === 'ar' ? '⚠️ تعذّر سحب بيانات المعروضات من قاعدة بيانات التجار' : '⚠️ Failed to load merchant listings data'}
            </p>
            <p className="text-xs text-red-300/80 mt-1 break-all">
              {error.code ? `HTTP ${error.code} · ` : ''}{String(error.detail).slice(0, 200)}
            </p>
          </div>
          <button
            type="button"
            onClick={fetchStats}
            disabled={loading}
            className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white text-xs hover:bg-white/10 flex items-center gap-1.5"
            data-testid="listings-stats-retry-btn"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
            {language === 'ar' ? 'إعادة المحاولة' : 'Retry'}
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {ORDER.map((st, idx) => {
          const m = TYPE_META[st];
          const Icon = m.icon;
          const count = per[st] ?? 0;
          return (
            <motion.button
              key={st}
              type="button"
              onClick={() => onShortcut?.(m.target)}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.04 }}
              whileHover={{ y: -2 }}
              className={`relative overflow-hidden text-start p-3.5 rounded-xl bg-gradient-to-br ${m.ringFrom} ${m.ringTo} border ${m.border} hover:shadow-lg transition-all`}
              data-testid={`stat-shortcut-${st}`}
            >
              <div className="flex items-center justify-between mb-2">
                <Icon className={`h-4 w-4 ${m.text}`} />
                {loading && <Loader2 className="h-3 w-3 text-white/30 animate-spin" />}
              </div>
              <div className={`text-2xl font-bold tabular-nums ${m.text}`}>
                {error ? '—' : count}
              </div>
              <div className="text-[10px] text-white/60 mt-0.5 truncate">
                {language === 'ar' ? m.ar : m.en}
              </div>
            </motion.button>
          );
        })}

        {/* Grand total */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="relative overflow-hidden p-3.5 rounded-xl bg-gradient-to-br from-[#d4af37]/30 to-[#d4af37]/5 border border-[#d4af37]/50"
          data-testid="stat-shortcut-total"
        >
          <div className="flex items-center justify-between mb-2">
            <Layers className="h-4 w-4 text-[#d4af37]" />
            {loading && <Loader2 className="h-3 w-3 text-white/30 animate-spin" />}
          </div>
          <div className="text-2xl font-bold tabular-nums text-[#d4af37]">
            {error ? '—' : total}
          </div>
          <div className="text-[10px] text-white/60 mt-0.5">
            {language === 'ar' ? 'المجموع الكلّي' : 'Grand Total'}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
